import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getThemeAssets } from '../utils/theme';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const leftSideRef = useRef<HTMLDivElement>(null);

  // Get theme assets from environment
  const themeAssets = getThemeAssets();

  // Get reset token from URL
  // Format: ?token=uuid&type=crm
  const resetToken = searchParams.get('token') || '';

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (leftSideRef.current) {
        const rect = leftSideRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    const leftSide = leftSideRef.current;
    if (leftSide) {
      leftSide.addEventListener('mousemove', handleMouseMove);
      return () => leftSide.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  useEffect(() => {
    if (!resetToken) {
      setError('Invalid or expired reset link. Please request a new one.');
    }
  }, [resetToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (!/^(?=.*[a-zA-Z])(?=.*\d).{8,}$/.test(newPassword)) {
      setError('Password must be at least 8 characters with letters and numbers');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: resetToken,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Password reset failed');
      }

      // Success - redirect to login page
      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Password reset failed. Please try again or request a new reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen w-screen flex flex-col-reverse lg:flex-row m-0 p-0">
      {/* Left Side - Glass Morphism Cards with Background (Bottom on mobile, Left on desktop) */}
      <div ref={leftSideRef} className="w-full h-[100vh] lg:w-1/2 lg:h-screen relative overflow-hidden">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-login-bgLeft"></div>

        {/* Animated Decoration Images */}
        <div
          className="absolute w-24 h-24 transition-transform duration-1000 ease-out opacity-80 hover:opacity-100"
          style={{
            transform: `translate(${mousePosition.x * 60}px, ${mousePosition.y * 40}px) rotate(${mousePosition.x * 10}deg)`,
            top: '10%',
            left: '10%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-16 h-16 transition-transform duration-800 ease-out opacity-60 hover:opacity-90"
          style={{
            transform: `translate(${mousePosition.x * -40}px, ${mousePosition.y * 60}px) rotate(${mousePosition.y * 15}deg)`,
            top: '65%',
            right: '15%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-14 h-14 transition-transform duration-1200 ease-out opacity-50 hover:opacity-80"
          style={{
            transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * -35}px) rotate(${mousePosition.x * -8}deg)`,
            top: '40%',
            left: '75%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-12 py-16">
          {/* Main Heading */}
          <div className="text-center w-full max-w-lg">
            <div className="mb-8 flex justify-center">
              <div className="p-6 bg-white/10 backdrop-blur-lg rounded-full border border-white/20">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="text-5xl font-bold mb-4 text-white leading-tight">Set New Password</h1>
            <p className="text-xl text-blue-100/90 font-light mb-2">Choose a strong password</p>
            <p className="text-base text-blue-200/80">
              Your new password must be different from previously used passwords and contain at least 8 characters.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Password Form (Top on mobile, Right on desktop) */}
      <div className="w-full lg:w-1/2 lg:h-screen bg-white flex items-center justify-center px-4 py-8 sm:px-6 lg:px-0">
        <div className="w-full max-w-md mx-auto lg:mx-8">
          <div className="">
            <div className="text-center mb-6">
              <img
                src={themeAssets.loginLogo}
                alt={themeAssets.brandName}
                className="h-10 sm:h-12 mx-auto mb-4"
              />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-2">
                Create New Password
              </h1>
              <p className="text-gray-600">Enter your new password below</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters with letters & numbers"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? '👁️' : '🔒'}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters with letters and numbers</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your new password"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showConfirmPassword ? '👁️' : '🔒'}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-800 mb-2">Password Requirements:</p>
                  <div className="space-y-1">
                    <div className="flex items-center text-xs">
                      {newPassword.length >= 8 ? (
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={newPassword.length >= 8 ? 'text-green-700' : 'text-gray-600'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      {/[a-zA-Z]/.test(newPassword) ? (
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={/[a-zA-Z]/.test(newPassword) ? 'text-green-700' : 'text-gray-600'}>
                        Contains letters
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      {/\d/.test(newPassword) ? (
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={/\d/.test(newPassword) ? 'text-green-700' : 'text-gray-600'}>Contains numbers</span>
                    </div>
                    <div className="flex items-center text-xs">
                      {newPassword === confirmPassword && confirmPassword ? (
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={newPassword === confirmPassword && confirmPassword ? 'text-green-700' : 'text-gray-600'}>
                        Passwords match
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !resetToken}
                className="w-full bg-brandColor text-white py-3 px-4 rounded-lg font-medium hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>

            {/* Back to Sign In */}
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
